import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, ChevronRight } from 'lucide-react';
import { getSmartNudges, markNudgeShown } from '@/utils/smartNudges';

/**
 * Smart Nudges Component
 * Displays gentle, behavior-based suggestions
 * Can be used as Banner (top of page) or Widget (in dashboard)
 */

const SmartNudges = ({ variant = 'banner', maxNudges = 2 }) => {
  const navigate = useNavigate();
  const [nudges, setNudges] = useState([]);
  const [dismissedNudges, setDismissedNudges] = useState(new Set());

  const loadNudges = useCallback(() => {
    // In production, pass real user data
    const generatedNudges = getSmartNudges();
    setNudges(generatedNudges.slice(0, maxNudges));
  }, [maxNudges]);

  useEffect(() => {
    loadNudges();
    
    // Refresh nudges every 30 minutes
    const interval = setInterval(loadNudges, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [loadNudges]);

  const handleDismiss = (nudge) => {
    setDismissedNudges(prev => new Set([...prev, nudge.type]));
    markNudgeShown(nudge.type);
  };

  const handleAction = (nudge) => {
    markNudgeShown(nudge.type);
    if (nudge.action.route) {
      navigate(nudge.action.route);
    }
  };

  // Filter out dismissed nudges
  const visibleNudges = nudges.filter(n => !dismissedNudges.has(n.type));

  if (visibleNudges.length === 0) {
    return null;
  }

  // Banner variant (top of page, prominent)
  if (variant === 'banner') {
    return (
      <div className="space-y-3">
        {visibleNudges.map((nudge, index) => (
          <Card
            key={nudge.type}
            className={`border-2 shadow-md animate-in slide-in-from-top duration-500 ${
              nudge.tone === 'motivating' || nudge.tone === 'celebrating'
                ? 'border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50'
                : nudge.tone === 'caring'
                ? 'border-purple-300 bg-gradient-to-r from-purple-50 to-pink-50'
                : 'border-blue-300 bg-gradient-to-r from-blue-50 to-cyan-50'
            }`}
            style={{ animationDelay: `${index * 150}ms` }}
          >
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="bg-white rounded-full p-3 flex-shrink-0 shadow-sm">
                  <span className="text-3xl">{nudge.icon}</span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {nudge.title}
                  </h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {nudge.message}
                  </p>

                  {/* Action Button */}
                  {nudge.action && (
                    <Button
                      onClick={() => handleAction(nudge)}
                      className="mt-3 h-9 text-sm bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
                    >
                      {nudge.action.text}
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                </div>

                {/* Dismiss Button */}
                {nudge.dismissible && (
                  <button
                    onClick={() => handleDismiss(nudge)}
                    className="flex-shrink-0 p-2 hover:bg-white/50 rounded-full transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Widget variant (compact, for dashboard)
  if (variant === 'widget') {
    return (
      <Card className="border-2 border-blue-200 shadow-md">
        <CardContent className="pt-5 pb-5">
          <div className="space-y-3">
            {visibleNudges.map((nudge) => (
              <div
                key={nudge.type}
                className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors cursor-pointer"
                onClick={() => handleAction(nudge)}
              >
                <span className="text-2xl flex-shrink-0">{nudge.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {nudge.title}
                  </p>
                  <p className="text-xs text-gray-600 truncate">
                    {nudge.message}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-blue-600 flex-shrink-0" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Notification variant (small, non-intrusive)
  if (variant === 'notification') {
    return (
      <div className="fixed bottom-4 right-4 space-y-2 z-50 max-w-sm">
        {visibleNudges.map((nudge, index) => (
          <div
            key={nudge.type}
            className="bg-white border-2 border-blue-300 rounded-lg shadow-lg p-4 animate-in slide-in-from-right duration-300"
            style={{ animationDelay: `${index * 200}ms` }}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl flex-shrink-0">{nudge.icon}</span>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 text-sm mb-1">
                  {nudge.title}
                </h4>
                <p className="text-xs text-gray-600 mb-2">
                  {nudge.message}
                </p>
                {nudge.action && (
                  <Button
                    onClick={() => handleAction(nudge)}
                    size="sm"
                    className="h-7 text-xs"
                  >
                    {nudge.action.text}
                  </Button>
                )}
              </div>
              {nudge.dismissible && (
                <button
                  onClick={() => handleDismiss(nudge)}
                  className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

/**
 * Widget version for Dashboard
 */
export const SmartNudgesWidget = () => {
  return <SmartNudges variant="widget" maxNudges={1} />;
};

/**
 * Banner version for top of pages
 */
export const SmartNudgesBanner = () => {
  return <SmartNudges variant="banner" maxNudges={1} />;
};

/**
 * Notification version (bottom-right corner)
 */
export const SmartNudgesNotification = () => {
  return <SmartNudges variant="notification" maxNudges={1} />;
};

export default SmartNudges;
